/**
 * tutorNav — resolves a tutor citation/action's `section_id` (+ optional
 * finer `sub_para_id`/`block_id` anchor) to a reader URL slug + DOM fragment,
 * reusing TASK-009's existing thin-client TOC resolution (NOT a new slug
 * scheme). This is the TASK-010 Phase 3 "deep-link" contract.
 *
 * Deliberately reuses `findSectionForSubParaId` from ./adapters.js (same
 * hierarchical dotted-id prefix matching already used by the Padronanza
 * "Ripassa" deep-link, see Reader.tsx `handleReviewFromMastery`) instead of
 * re-deriving section/slug resolution — the thin client has no full manuale
 * to walk, and this logic is already correct and tested for that shape.
 *
 * Anchor granularity depends on what the caller has available:
 *   - Live SSE `citation` events and persisted citations (loaded via GET
 *     .../conversations/:id) both carry `sub_para_id`/`block_id`, but as
 *     OPTIONAL/nullable (a hit that couldn't resolve a fine anchor omits
 *     them) -> block-level anchor via `blockDomId` when present, else
 *     falls back to section-level navigation.
 *   - The `open_section` action event only ever carries `section_id` (API
 *     Contract) -> always section-level.
 */

import { blockDomId } from '@arianna/reader-core'
import { findSectionForSubParaId, type TocAdapterResult } from './adapters.js'

export interface TutorNavTarget {
  slug: string
  /** '' or a '#dom-id' fragment (see blockDomId). */
  anchor: string
}

export function resolveTutorSectionTarget(
  toc: TocAdapterResult,
  sectionId: string,
  fineId?: string | null,
): TutorNavTarget | null {
  const fine = fineId ?? null
  const fineTarget = fine != null ? findSectionForSubParaId(toc, fine) : null
  const section = fineTarget ?? toc.flatSections.find((s) => s.id === sectionId) ?? null
  if (section == null) return null

  const anchor = fine != null && fine !== section.id ? `#${blockDomId(fine)}` : ''
  return { slug: section.slug, anchor }
}

// ---------------------------------------------------------------------------
// resolveTutorSectionTargetFromSlugs — same resolution contract as
// resolveTutorSectionTarget above (identical signature/semantics: exact fine
// id match first, else longest dotted-prefix match, else the bare
// section_id; anchor omitted when the fine id IS the resolved section id),
// but built for pages/reader/Tutor.tsx (TASK-014 Step B2 fix), which has NO
// full TOC to walk (task doc invariant: "ogni pagina riceve solo i dati
// della propria vista") — only the lightweight `sectionSlugs` id->slug map
// ReaderController#tutor now sends. Reuses the exact same hierarchical
// dotted-id prefix-matching rule as findSectionForSubParaId (see that
// function's doc), just applied to a plain object's keys instead of
// TocAdapterResult.flatSections.
// ---------------------------------------------------------------------------

function findSectionIdForSubParaId(
  sectionIds: readonly string[],
  subParaId: string,
): string | null {
  if (sectionIds.includes(subParaId)) return subParaId

  let best: string | null = null
  for (const id of sectionIds) {
    if (!subParaId.startsWith(`${id}.`)) continue
    if (best == null || id.length > best.length) best = id
  }
  return best
}

export function resolveTutorSectionTargetFromSlugs(
  sectionSlugs: Readonly<Record<string, string>>,
  sectionId: string,
  fineId?: string | null,
): TutorNavTarget | null {
  const fine = fineId ?? null
  const sectionIds = Object.keys(sectionSlugs)

  const resolvedId =
    fine != null ? (findSectionIdForSubParaId(sectionIds, fine) ?? sectionId) : sectionId
  const slug = sectionSlugs[resolvedId]
  if (slug == null) return null

  const anchor = fine != null && fine !== resolvedId ? `#${blockDomId(fine)}` : ''
  return { slug, anchor }
}
